import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ContractExtractionProgressProps {
  active: boolean;
  fileName?: string;
  variant?: "dialog" | "inline";
}

const EXPECTED_SECONDS = 15;
const TICK_MS = 200;

export function ContractExtractionProgress({
  active,
  fileName,
  variant = "dialog",
}: ContractExtractionProgressProps) {
  const [pct, setPct] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setPct(0);
      setElapsed(0);
      return;
    }
    setPct(4);
    setElapsed(0);
    const started = Date.now();
    const id = window.setInterval(() => {
      const secs = (Date.now() - started) / 1000;
      setElapsed(secs);
      const ratio = secs / EXPECTED_SECONDS;
      const eased = 1 - Math.exp(-1.6 * ratio);
      const next = Math.min(95, Math.round(eased * 95));
      setPct((prev) => (next > prev ? next : prev));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active) return null;

  const stage =
    elapsed < 4
      ? "Reading the PDF…"
      : elapsed < 9
      ? "Parsing contract terms with AI…"
      : elapsed < 14
      ? "Pulling out dates, prices, and contacts…"
      : "Almost done…";

  const containerClass =
    variant === "dialog"
      ? "space-y-4 py-8 px-4 text-center"
      : "space-y-3 rounded-lg border border-blue-200 bg-blue-50/60 p-4";

  return (
    <div className={containerClass} data-testid="contract-extraction-progress">
      <div className={variant === "dialog" ? "flex flex-col items-center gap-3" : "flex items-start gap-3"}>
        <div className="relative shrink-0">
          <Loader2
            className={
              variant === "dialog"
                ? "h-12 w-12 text-purple-500 animate-spin"
                : "h-8 w-8 text-blue-600 animate-spin"
            }
          />
          <Sparkles
            className={
              variant === "dialog"
                ? "h-5 w-5 text-purple-500 absolute -right-1 -top-1"
                : "h-3.5 w-3.5 text-blue-500 absolute -right-0.5 -top-0.5"
            }
          />
        </div>
        <div className={variant === "dialog" ? "space-y-1" : "flex-1 min-w-0 space-y-1"}>
          <p
            className={
              variant === "dialog"
                ? "text-base font-semibold text-gray-800"
                : "text-sm font-semibold text-gray-800"
            }
            data-testid="text-extraction-stage"
          >
            {stage}
          </p>
          <p className="text-xs text-gray-600">
            This usually takes about {EXPECTED_SECONDS} seconds. Please keep this {variant === "dialog" ? "window" : "page"} open
            {fileName ? ` — extracting "${fileName}"` : ""}.
          </p>
        </div>
      </div>
      <div className={variant === "dialog" ? "max-w-sm mx-auto space-y-1" : "space-y-1"}>
        <Progress value={pct} className="h-2" data-testid="progress-extraction" />
        <div className="flex items-center justify-between text-[11px] text-gray-500">
          <span data-testid="text-extraction-percent">{pct}%</span>
          <span>{Math.min(EXPECTED_SECONDS + 5, Math.round(elapsed))}s elapsed</span>
        </div>
      </div>
    </div>
  );
}
